import { Lock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNetworkShipperName } from "@/hooks/useNetworkShipperName";

interface PrivateNetworkBadgeProps {
  networkId: string | null | undefined;
  /** "compact" = table cell, "card" = mobile card, "banner" = detail-panel banner */
  variant?: "compact" | "card" | "banner";
  className?: string;
}

export function PrivateNetworkBadge({
  networkId,
  variant = "compact",
  className,
}: PrivateNetworkBadgeProps) {
  const { data: shipperName } = useNetworkShipperName(networkId);
  const name = shipperName || "Private Network";
  const isFallback = name === "Private Network";

  if (variant === "banner") {
    return (
      <div
        className={cn(
          "rounded-md border border-brand-teal/30 bg-brand-teal/10 px-3 py-2 text-[11px] text-brand-teal flex items-center gap-1.5 font-semibold uppercase tracking-wide",
          className,
        )}
      >
        <Lock className="h-3 w-3" />
        {isFallback
          ? "Private bid — you're in this shipper's trusted network"
          : `Private bid — posted by ${name}`}
      </div>
    );
  }

  if (variant === "card") {
    return (
      <Badge
        className={cn(
          "bg-brand-teal/15 text-brand-teal border-0 text-[9px] uppercase font-bold gap-1 px-1.5 h-4 max-w-full",
          className,
        )}
      >
        <Lock className="h-2.5 w-2.5 shrink-0" />
        {/* Network NAME is revealed only to accepted members (enforced server
            side by get_network_shipper_name); everyone else sees "Private". */}
        <span className="truncate">{isFallback ? "Private" : `Private — ${name}`}</span>
      </Badge>
    );
  }

  return (
    <Badge
      className={cn(
        "bg-brand-teal/15 text-brand-teal border-0 text-[9px] px-1.5 py-0 h-4 font-bold uppercase gap-0.5 max-w-[190px]",
        className,
      )}
      title={isFallback ? "Private network load" : `Private — ${name}`}
    >
      <Lock className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{isFallback ? "Private" : `Private — ${name}`}</span>
    </Badge>
  );
}
